import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { captureException } from "@sentry/react";
import { Button } from "@aidkitorg/component-library";
import * as v0 from "@aidkitorg/types/lib/survey";
import InterfaceContext from "../Context";
import { useModularMarkdown } from "../Hooks/ModularMarkdown";
import { usePost } from "../API";
import { DistroQuestionProps } from "./Props";
import { useLocalizedStrings } from "../Localization";

const ARGYLE_SDK_SRC = "https://plugin.argyle.com/argyle.web.v5.js";

type ArgyleLinkInstance = {
  open: () => void;
  exit?: () => void;
  destroy?: () => void;
};

type ArgyleAccountConnectedPayload = {
  accountId: string;
  userId: string;
};

type ArgyleAccountErrorPayload = {
  accountId?: string;
  errorCode?: string;
  errorMessage?: string;
  userId?: string;
};

type ArgyleDDSResultPayload = {
  accountId?: string;
  userId?: string;
};

type ArgyleCreateOptions = {
  apiKey?: string;
  userToken: string;
  sandbox?: boolean;
  language?: string;
  flowId?: string;
  onAccountConnected: (payload: ArgyleAccountConnectedPayload) => void | Promise<void>;
  onTokenExpired: (updateToken: (token: string) => void) => void | Promise<void>;
  onAccountCreated?: (payload: { userId: string }) => void;
  onAccountRemoved?: (payload: { accountId: string; userId: string }) => void;
  onAccountError?: (payload: ArgyleAccountErrorPayload) => void;
  onDDSSuccess?: (payload: ArgyleDDSResultPayload) => void;
  onDDSError?: (payload: ArgyleAccountErrorPayload) => void;
  onExit?: () => void;
  onClose?: () => void;
};

type CreateUserTokenResponse = {
  user_token: string;
  user_id: string;
};

declare global {
  interface Window {
    Argyle?: {
      create: (options: ArgyleCreateOptions) => ArgyleLinkInstance;
    };
  }
}

function useArgyleScript() {
  const [ready, setReady] = useState(() => typeof window !== "undefined" && !!window.Argyle);
  const [error, setError] = useState<Error | null>(null);

  useEffect(() => {
    if (ready || typeof window === "undefined") {
      return;
    }

    let cancelled = false;
    const existingScript = document.querySelector<HTMLScriptElement>("script[data-argyle-sdk]");

    if (existingScript) {
      const handleLoad = () => !cancelled && setReady(true);
      const handleError = () => {
        if (!cancelled) {
          const err = new Error("Failed to load Argyle SDK");
          setError(err);
          captureException(err);
        }
      };

      existingScript.addEventListener("load", handleLoad);
      existingScript.addEventListener("error", handleError);

      return () => {
        cancelled = true;
        existingScript.removeEventListener("load", handleLoad);
        existingScript.removeEventListener("error", handleError);
      };
    }

    const script = document.createElement("script");
    script.async = true;
    script.src = ARGYLE_SDK_SRC;
    script.dataset.argyleSdk = "true";

    const handleLoad = () => {
      if (!cancelled) {
        setReady(true);
      }
    };

    const handleError = () => {
      if (!cancelled) {
        const err = new Error("Failed to load Argyle SDK");
        setError(err);
        captureException(err);
      }
    };

    script.addEventListener("load", handleLoad);
    script.addEventListener("error", handleError);

    document.body.appendChild(script);

    return () => {
      cancelled = true;
      script.removeEventListener("load", handleLoad);
      script.removeEventListener("error", handleError);
    };
  }, [ready]);

  return { ready, error };
}

export function ArgyleIncomeVerification({
  component,
  info,
  setInfo,
  uid,
}: DistroQuestionProps<v0.ArgyleVerification>) {
  const L = useLocalizedStrings();
  const context = useContext(InterfaceContext);
  const createUserToken = usePost("/argyle/create_user_token");
  const saveConnectionDetails = usePost("/argyle/save_connection_details");
  const getArgyleData = usePost("/argyle/get_argyle_data");
  const [isLaunching, setIsLaunching] = useState(false);
  const [argyleError, setArgyleError] = useState<string | null>(null);
  const [pollingTimedOut, setPollingTimedOut] = useState(false);
  const pollingStartTimeRef = useRef<number | null>(null);
  const latestUserIdRef = useRef<string | null>(info[component.targetField + "_user_id"] || null);
  const argyleInstanceRef = useRef<ArgyleLinkInstance | null>(null);
  const hasStartedNewConnectionRef = useRef<boolean>(false);
  const connectionSucceededRef = useRef<boolean>(false);
  const { ready: sdkReady, error: sdkLoadError } = useArgyleScript();

  const header = useModularMarkdown({ content: component.header, info });
  const questionDescription = useModularMarkdown({ content: component.questionDescription, info });
  const connectedDescription = useModularMarkdown({ content: component.connectedDescription, info });
  const optedOutDescription = useModularMarkdown({ content: component.optedOutDescription, info });
  const verifyButtonText = useModularMarkdown({ content: component.verifyButtonText, info, noParagraphs: true });
  const reverifyButtonText = useModularMarkdown({ content: component.reverifyButtonText, info, noParagraphs: true });
  const optOutButtonText = useModularMarkdown({ content: component.optOutButtonText, info, noParagraphs: true });

  const currentState = info[component.targetField] || "";
  const userId = info[component.targetField + "_user_id"] || "";
  const minHoursCompletedRaw = info[component.targetField + "_min_hours_completed"] || "";

  const minHoursData = useMemo(() => {
    if (!minHoursCompletedRaw) {
      return null;
    }
    try {
      return JSON.parse(minHoursCompletedRaw);
    } catch (error) {
      console.error("Failed to parse min_hours_completed", error);
      return null;
    }
  }, [minHoursCompletedRaw]);

  // Create enriched info with min hours data for use in templates
  const enrichedInfo = useMemo(() => {
    if (!minHoursData) {
      return info;
    }
    return {
      ...info,
      _totalHoursWorked: String(minHoursData.totalHoursWorked ?? 0),
      _minHoursRequired: String(minHoursData.minHoursRequired ?? 0),
      _lookbackDays: String(minHoursData.lookbackDays ?? 0),
      _meetsMinHoursRequirement: String(minHoursData.meetsMinHoursRequirement ?? false),
    };
  }, [info, minHoursData]);

  const minHoursMetText = useModularMarkdown({ content: component.minHoursMetText, info: enrichedInfo });
  const minHoursNotMetText = useModularMarkdown({ content: component.minHoursNotMetText, info: enrichedInfo });

  const description = useMemo(() => {
    switch (currentState) {
      case "pending":
      case "processed":
        return connectedDescription;
      case "opted_out":
        return optedOutDescription;
      default:
        return questionDescription;
    }
  }, [currentState, questionDescription, connectedDescription, optedOutDescription]);

  // Only show re-verify button if data has been processed (not still pending without results)
  const isProcessed = currentState === "processed" || (currentState === "pending" && minHoursCompletedRaw);
  const primaryButtonText = isProcessed ? reverifyButtonText : verifyButtonText;

  const teardownInstance = useCallback(() => {
    argyleInstanceRef.current?.exit?.();
    argyleInstanceRef.current?.destroy?.();
    argyleInstanceRef.current = null;
  }, []);

  useEffect(() => () => {
    teardownInstance();
  }, [teardownInstance]);

  const requestUserToken = useCallback(async () => {
    const response = await createUserToken({
      external_user_id: uid || undefined,
      target_field: component.targetField,
    });

    if (!response) {
      throw new Error("No response when creating Argyle user token");
    }

    const { user_token: userToken, user_id: userId } = response as CreateUserTokenResponse;
    console.log("[ArgyleIncomeVerification] Created user token - user_id:", userId);
    latestUserIdRef.current = userId;
    return { userToken, userId };
  }, [createUserToken, uid, component.targetField]);

  const handleAccountConnected = useCallback(async ({ userId, accountId }: ArgyleAccountConnectedPayload) => {
    try {
      console.log("[ArgyleIncomeVerification] Account connected - user_id:", userId, "account_id:", accountId);
      console.log("[ArgyleIncomeVerification] Expected user_id from createUserToken:", latestUserIdRef.current);
      await saveConnectionDetails({
        user_id: userId,
        account_id: accountId,
        target_field: component.targetField,
      });

      setInfo(component.targetField, "pending");
      setInfo(component.targetField + "_user_id", userId);
      setInfo(component.targetField + "_paystub_data", "");
      setInfo(component.targetField + "_min_hours_completed", "");
      setInfo(component.targetField + "_error", "");
      latestUserIdRef.current = userId;
      connectionSucceededRef.current = true;
      setArgyleError(null);
    } catch (error) {
      captureException(error as Error);
      setArgyleError("We couldn't save your connection. Please try again.");
    } finally {
      setIsLaunching(false);
      teardownInstance();
    }
  }, [component.targetField, saveConnectionDetails, setInfo, teardownInstance]);

  const argyleApiKey = process.env.REACT_APP_ARGYLE_API_KEY;
  const isSandbox = process.env.NODE_ENV !== "production";

  const accountRemovedRef = useRef<boolean>(false);

  const handleExitOrClose = useCallback(() => {
    setIsLaunching(false);

    // Clear data if:
    // 1. They started a new connection but didn't complete it, OR
    // 2. They removed an account during re-verification
    // This preserves data if they just open and close the modal without doing anything
    if ((hasStartedNewConnectionRef.current && !connectionSucceededRef.current) || accountRemovedRef.current) {
      setInfo(component.targetField, "");
      setInfo(component.targetField + "_user_id", "");
      setInfo(component.targetField + "_paystub_data", "");
      setInfo(component.targetField + "_min_hours_completed", "");
      setInfo(component.targetField + "_error", "");
      accountRemovedRef.current = false;
    }

    teardownInstance();
  }, [component.targetField, setInfo, teardownInstance]);

  const deriveLanguage = useCallback(() => {
    const lang = context.lang?.toUpperCase?.();
    if (!lang) {
      return undefined;
    }
    switch (lang) {
      case "EN":
      case "ES":
      case "RU":
      case "ZH":
        return lang;
      default:
        return "EN";
    }
  }, [context.lang]);

  const launchArgyle = useCallback(async () => {
    if (!sdkReady) {
      setArgyleError("We're still getting things ready. Please try again in a moment.");
      return;
    }

    if (!window.Argyle) {
      setArgyleError("We couldn't load Argyle right now. Please refresh and try again.");
      return;
    }

    setIsLaunching(true);
    setArgyleError(null);

    // Reset tracking flags
    hasStartedNewConnectionRef.current = false;
    connectionSucceededRef.current = false;
    accountRemovedRef.current = false;

    try {
      const { userToken } = await requestUserToken();

      if (argyleInstanceRef.current) {
        argyleInstanceRef.current.exit?.();
        argyleInstanceRef.current.destroy?.();
      }

      const config: ArgyleCreateOptions = {
        userToken,
        sandbox: isSandbox,
        language: deriveLanguage(),
        onAccountConnected: handleAccountConnected,
        onTokenExpired: async (updateToken) => {
          try {
            const refreshed = await requestUserToken();
            updateToken(refreshed.userToken);
          } catch (error) {
            captureException(error as Error);
            setArgyleError("We couldn't refresh the Argyle session. Please try again.");
          }
        },
        onAccountCreated: (payload) => {
          latestUserIdRef.current = payload.userId;
          hasStartedNewConnectionRef.current = true;
        },
        onAccountRemoved: (payload) => {
          console.log("[ArgyleIncomeVerification] Account removed - user_id:", payload.userId, "account_id:", payload.accountId);
          accountRemovedRef.current = true;
        },
        onAccountError: (payload) => {
          const message = payload?.errorMessage || "We couldn't connect your account. Please try again.";
          setArgyleError(message);
          const error = new Error(`Argyle account error: ${payload?.errorCode || "unknown"}`);
          (error as any).extra = payload;
          captureException(error);
        },
        onDDSSuccess: () => {
          setArgyleError(null);
        },
        onDDSError: (payload) => {
          const message = payload?.errorMessage || "We couldn't complete the deposit switch. Please try again.";
          setArgyleError(message);
          const error = new Error(`Argyle DDS error: ${payload?.errorCode || "unknown"}`);
          (error as any).extra = payload;
          captureException(error);
        },
        onExit: handleExitOrClose,
        onClose: handleExitOrClose,
      };

      if (argyleApiKey) {
        config.apiKey = argyleApiKey;
      }

      if (component.flowId) {
        config.flowId = component.flowId;
      }

      argyleInstanceRef.current = window.Argyle.create(config);

      argyleInstanceRef.current.open();

    } catch (error) {
      captureException(error as Error);
      setArgyleError("We couldn't start Argyle right now. Please try again.");
      setIsLaunching(false);
    }
  }, [sdkReady, argyleApiKey, requestUserToken, handleAccountConnected, handleExitOrClose, isSandbox, deriveLanguage]);

  const handleClickVerify = useCallback(() => {
    if (isLaunching) {
      return;
    }

    void launchArgyle();
  }, [isLaunching, launchArgyle]);

  const handleClickOptOut = useCallback(() => {
    teardownInstance();
    setInfo(component.targetField, "opted_out");
    setInfo(component.targetField + "_user_id", "");
    setInfo(component.targetField + "_paystub_data", "");
    setInfo(component.targetField + "_error", "");
    setArgyleError(null);
  }, [component.targetField, setInfo, teardownInstance]);

  useEffect(() => {
    if (sdkLoadError) {
      setArgyleError("We couldn't load Argyle. Please refresh and try again.");
    }
  }, [sdkLoadError]);

  // Poll for Argyle processing results when in pending state
  useEffect(() => {
    // Only start polling if we have a userId and we're in pending state without results
    // AND the modal is not currently open (isLaunching = false)
    if (currentState !== "pending" || minHoursCompletedRaw || !userId || pollingTimedOut || isLaunching) {
      return;
    }

    const POLLING_TIMEOUT_MS = 5 * 60 * 1000; // 5 minutes
    const BASE_INTERVAL_MS = 2500; // 2.5 seconds
    const MAX_INTERVAL_MS = 20000; // 20 seconds max
    let timeoutId: NodeJS.Timeout | null = null;
    let consecutiveErrors = 0;
    const MAX_CONSECUTIVE_ERRORS = 5;
    let currentInterval = BASE_INTERVAL_MS;

    if (!pollingStartTimeRef.current) {
      pollingStartTimeRef.current = Date.now();
      setPollingTimedOut(false);
    }

    const scheduleNextCheck = () => {
      timeoutId = setTimeout(checkArgyleData, currentInterval);
    };

    const checkArgyleData = async () => {
      // Check if we've exceeded the timeout
      if (pollingStartTimeRef.current && Date.now() - pollingStartTimeRef.current > POLLING_TIMEOUT_MS) {
        console.log("[ArgyleIncomeVerification] Polling timeout reached");
        setPollingTimedOut(true);
        return;
      }

      // Circuit breaker: stop polling after too many consecutive errors
      if (consecutiveErrors >= MAX_CONSECUTIVE_ERRORS) {
        console.error("[ArgyleIncomeVerification] Too many consecutive errors, stopping polling");
        setArgyleError("We're having trouble checking your verification status. Please refresh the page.");
        return;
      }

      console.log("[ArgyleIncomeVerification] Checking Argyle data for user:", userId);

      try {
        const resp = await getArgyleData({
          user_id: userId,
          target_field: component.targetField,
        });

        console.log("[ArgyleIncomeVerification] Got response:", resp);

        // Reset error counter and interval on successful response
        consecutiveErrors = 0;
        currentInterval = BASE_INTERVAL_MS;

        // If processing is done, apply the updates and stop polling
        if (resp?.kind === "done" && resp.updates) {
          console.log("[ArgyleIncomeVerification] Applying updates:", resp.updates);

          // Apply all updates
          Object.entries(resp.updates).forEach(([key, value]) => {
            setInfo(key, value || "");
          });

          setArgyleError(null);
          pollingStartTimeRef.current = null;
          console.log("[ArgyleIncomeVerification] Data received, stopped polling");
          return; // Stop polling
        }

        // Schedule next check
        scheduleNextCheck();
      } catch (error) {
        consecutiveErrors++;
        console.error(`[ArgyleIncomeVerification] Error checking Argyle data (${consecutiveErrors}/${MAX_CONSECUTIVE_ERRORS}):`, error);
        captureException(error as Error);

        // Exponential backoff on errors: double the interval up to MAX_INTERVAL_MS
        currentInterval = Math.min(currentInterval * 2, MAX_INTERVAL_MS);
        console.log(`[ArgyleIncomeVerification] Backing off to ${currentInterval}ms after error`);

        // Schedule next check with backoff
        scheduleNextCheck();
      }
    };

    // Start polling when in pending state and modal is closed
    // Will restart if user reopens/closes modal or other dependencies change
    void checkArgyleData();

    return () => {
      if (timeoutId) clearTimeout(timeoutId);
      // Reset when component unmounts or polling stops
      if (currentState !== "pending") {
        pollingStartTimeRef.current = null;
      }
    };
  }, [currentState, minHoursCompletedRaw, userId, pollingTimedOut, isLaunching]);

  return (
    <fieldset>
      <legend>
        {header}
        {description}
      </legend>

      {userId && currentState === "pending" && !minHoursCompletedRaw && !pollingTimedOut && (
        <div className="mt-2 flex items-center gap-2 text-sm text-gray-600">
          <svg className="animate-spin h-4 w-4 text-gray-600" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
          </svg>
          <span>{L.questions.income_verification.fetching_payroll_data}</span>
        </div>
      )}

      {pollingTimedOut && currentState === "pending" && !minHoursCompletedRaw && (
        <div className="mt-2 p-3 rounded-md bg-blue-50 text-blue-800">
          <p className="text-sm">
            {L.questions.income_verification.still_processing_data}
          </p>
        </div>
      )}

      {minHoursData && minHoursData.meetsMinHoursRequirement !== null && (
        <div >
          {minHoursData.meetsMinHoursRequirement ? minHoursMetText : minHoursNotMetText}
        </div>
      )}

      {/* Only show verify/re-verify button if not currently fetching data */}
      {!(currentState === "pending" && !minHoursCompletedRaw && !pollingTimedOut) && (
        <div className="mt-2">
          <Button
            variant="primary"
            onClick={handleClickVerify}
            disabled={isLaunching}
          >
            {isLaunching ? "Connecting..." : primaryButtonText}
          </Button>
        </div>
      )}

      {!currentState &&
        <div className="mt-2">
          <Button
            variant="secondary"
            onClick={handleClickOptOut}
          >
            {optOutButtonText}
          </Button>
        </div>
      }

      {argyleError &&
        <p className="mt-2 text-sm text-red-600" role="alert">
          {argyleError}
        </p>
      }
    </fieldset>
  );
}
